<template>
  <Toast />
  <v-app>
    <v-main>
      <v-container class="general-style">
        <div class="beta-badge">
          beta
        </div>

        <!-- Desktop Layout -->
        <v-layout 
          wrap 
          class="layout-style desktop-layout"
        >
          <Tag class="app-name-eng"> 
            ImmersiveLang 
          </Tag>
          <v-flex 
            xs12 
            sm6 
            class="intro-column"
          >
            <AppIntroduction />
          </v-flex>

          <v-flex 
            xs12 
            sm6 
            class="login-column"
          >
            <Tag 
              class="app-name-jp"
            > 
              イマーシブラング
            </Tag>
            <v-card class="elevation-12 card-style">
              <v-toolbar 
                dark 
                color="primary"
              >
                <v-toolbar-title class="title-style">
                  Login
                </v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <v-alert
                  v-if="successMessage"
                  type="success"
                  variant="tonal"
                  class="mb-3"
                  density="compact"
                  :icon="false"
                >
                  {{ successMessage }}
                </v-alert>
                <form 
                  ref="form" 
                  @submit.prevent="login()"
                >
                  <v-text-field
                    v-model="email"
                    name="email"
                    label="Email"
                    type="text"
                    required
                  />

                  <v-text-field
                    v-model="password"
                    name="password"
                    label="Password"
                    type="password"
                    required
                  />
                  <v-btn
                    type="submit"
                    class="ns-btn-primary mt-4"
                    :elevation="0"
                    value="log in"
                    :disabled="isLoading"
                  >
                    <v-progress-circular
                      v-if="isLoading"
                      indeterminate
                      size="20"
                      width="2"
                      class="mr-2"
                      color="white"
                    />
                    Login
                  </v-btn>
                  <div style="padding-top: 20px; text-align: right;">
                    <span
                      class="auth-link"
                      @click="updatePassword()"
                    >
                      {{ FORGOT_PASSWORD_MSG }}
                    </span>
                  </div>
                  <div style="padding-top: 20px;">
                    <span
                      class="auth-link"
                      @click="openRegisterPage()"
                    >
                      {{ USER_REGISTRATION_MSG }}
                    </span>
                  </div>
                </form>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>

        <!-- Mobile Layout -->
        <div class="mobile-layout">
          <div
            v-if="!showLoginForm"
            class="mobile-intro-container"
          >
            <div class="mobile-intro-header">
              <Tag class="app-name-eng"> 
                ImmersiveLang 
              </Tag>
              <Tag class="app-name-jp"> 
                イマーシブラング
              </Tag>
            </div>
            <AppIntroduction />
            <v-btn
              class="ns-btn-primary mt-6"
              :elevation="0"
              style="width: 100%;"
              @click="showLoginForm = true"
            >
              Proceed to Login
            </v-btn>
          </div>

          <div
            v-if="showLoginForm"
            class="mobile-login-container"
          >
            <v-btn
              icon
              small
              class="back-button"
              @click="showLoginForm = false"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-card class="elevation-12 card-style">
              <v-toolbar 
                dark 
                color="primary"
              >
                <v-toolbar-title class="title-style">
                  Login
                </v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <v-alert
                  v-if="successMessage"
                  type="success"
                  variant="tonal"
                  class="mb-3"
                  density="compact"
                  :icon="false"
                >
                  {{ successMessage }}
                </v-alert>
                <form 
                  ref="form" 
                  @submit.prevent="login()"
                >
                  <v-text-field
                    v-model="email"
                    name="email"
                    label="Email"
                    type="text"
                    required
                  />

                  <v-text-field
                    v-model="password"
                    name="password"
                    label="Password"
                    type="password"
                    required
                  />
                  <v-btn
                    type="submit"
                    class="ns-btn-primary mt-4"
                    :elevation="0"
                    value="log in"
                    :disabled="isLoading"
                  >
                    <v-progress-circular
                      v-if="isLoading"
                      indeterminate
                      size="20"
                      width="2"
                      class="mr-2"
                      color="white"
                    />
                    Login
                  </v-btn>
                  <div style="padding-top: 20px; text-align: right;">
                    <span
                      class="auth-link"
                      @click="updatePassword()"
                    >
                      {{ FORGOT_PASSWORD_MSG }}
                    </span>
                  </div>
                  <div style="padding-top: 20px;">
                    <span
                      class="auth-link"
                      @click="openRegisterPage()"
                    >
                      {{ USER_REGISTRATION_MSG }}
                    </span>
                  </div>
                </form>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import Toast from "primevue/toast";
import AppIntroduction from "@/components/AppIntroduction.vue";
import {
  USER_REGISTRATION_MSG,
  FORGOT_PASSWORD_MSG,
  LOGIN_FAILED_MSG,
  LOAD_DATA_FAILED_ERR_MSG,
  FAILED_STATUS
} from "@/assets/constants/constants.js";
import {
  UPDATE_PASSWORD_API_ENDPOINT,
  BASE_URL
} from "@/assets/constants/api_endpoints.js";
import { post } from "@/services/api.js";
import { collapsed, toggleSidebar } from './sidebar/state';
import "../assets/css/common.css";

export default {
  name: "LoginForm",
  components: {
    Toast,
    AppIntroduction
  },
  props: {
    loggedOut: {
      type: Boolean,
      default: false,
    },
    infoMessage: {
      type: String,
      default: "",
    },
  },
  setup() {
    return {
      collapsed,
      toggleSidebar
    }
  },
  data() {
    return {
      email: "",
      password: "",
      showLoginForm: false,
      isLoading: false,
      successMessage: "",
      USER_REGISTRATION_MSG,
      FORGOT_PASSWORD_MSG,
      LOGIN_FAILED_MSG,
    };
  },
  mounted() {
    const message = this.infoMessage || this.$route.query.infoMessage;
    if (message) {
      this.successMessage = message;
    }
    if (this.loggedOut === "true") {
      this.showInfoNotification(FAILED_STATUS, LOAD_DATA_FAILED_ERR_MSG);
    }
    if (!this.collapsed) {
      this.toggleSidebar();
    }
  },
  methods: {
    showInfoNotification(status, message) {
      this.$toast.add({ severity: status, summary: message, life: 10000 });
    },
    extractLoginErrorMessage(error) {
      const responseData = error?.response?.data;

      if (!responseData) {
        return error?.message || "";
      }

      if (typeof responseData === "string") {
        return responseData;
      }

      if (Array.isArray(responseData.non_field_errors) && responseData.non_field_errors.length > 0) {
        return responseData.non_field_errors[0];
      }

      const firstKey = Object.keys(responseData)[0];
      if (!firstKey) {
        return error?.message || "";
      }

      const firstValue = responseData[firstKey];
      if (Array.isArray(firstValue) && firstValue.length > 0) {
        return String(firstValue[0]);
      }

      if (typeof firstValue === "string") {
        return firstValue;
      }

      return error?.message || "";
    },
    async handleLogin(authInfo) {
      await this.$store
        .dispatch("login", authInfo)
        .then(() => { this.$router.push({ path: "/userprofile" }); })
        .catch((err) => {
          const backendErrorMessage = this.extractLoginErrorMessage(err);
          this.showInfoNotification(FAILED_STATUS, backendErrorMessage || LOGIN_FAILED_MSG);
        });
    },
    async login() {
      this.isLoading = true;
      try {
        await this.handleLogin({
          user: {
            email: this.email,
            password: this.password,
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
    openRegisterPage() {
      this.$router.push({ path: "/register" });
    },
    async updatePassword() {

      if (!this.email) {
        this.showInfoNotification(FAILED_STATUS, "Please enter your email to update password.");
        return;
      }

      try {
        const userInfo = {
          email: this.email,
        };
        await post(BASE_URL, UPDATE_PASSWORD_API_ENDPOINT, userInfo);

        this.$router.push({ path: "/password" });
      } catch (error) {
        this.showInfoNotification(FAILED_STATUS, "Failed to send password update email. Please try again later.");
        return;
      }

    },
  },
};
</script>

<style scoped>
.title-style {
  color: white;
}

:deep(.v-toolbar__content > .v-toolbar-title) {
  margin-inline-start: 0;
}

.desktop-layout {
  display: flex;
}

@media (max-width: 599px) {
  .desktop-layout {
    display: none !important;
    visibility: hidden;
  }
}

.mobile-layout {
  display: none;
}

@media (max-width: 599px) {
  .mobile-layout {
    display: block;
    width: 100%;
    min-height: 100vh;
    height: 100dvh;
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  .mobile-intro-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    min-height: 100dvh;
    padding-bottom: 60px;
  }

  .mobile-intro-header {
    text-align: center;
    margin-bottom: 20px;
    width: 100%;
    flex-shrink: 0;
  }

  .mobile-login-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100dvh;
    position: relative;
  }

  .back-button {
    position: absolute;
    top: 20px;
    left: 16px;
  }

  .card-style {
    width: 100%;
    max-width: 100%;
    margin-top: 40px;
  }
}

.app-name-eng {
  position: fixed;
  top: 0;
  right: 50vw;
  font-size: 1.5rem;
  font-weight: 700;
  font-family:'Franklin Gothic Medium', 'Arial Narrow', Arial, sans-serif;
  color: #ffffff;
  text-shadow: 0px 0px 10px rgba(0,0,0,0.3)  ;
  z-index: 10;
  margin-top: 10px;
  margin-right: 12px;
}

.app-name-jp {
  position: fixed;
  top: 0;
  left: 50vw;
  font-size: 1.4rem;
  font-weight: 700;
  font-family: "ＭＳ Ｐゴシック";
  color: var(--color-btn-primary-bg);
  z-index: 10;
  margin-left: 10px;
  margin-top: 13px;
}

@media (max-width: 599px) {
  .app-name-eng {
    position: relative;
    top: auto;
    right: auto;
    margin: 0;
    padding: 0;
    font-size: 1.2rem;
    text-shadow: none;
    display: block;
    width: 100%;
    text-align: center;
  }

  .app-name-jp {
    position: relative;
    top: auto;
    left: auto;
    margin: 0;
    padding: 10px 0;
    font-size: 1.1rem;
    width: 100%;
    text-align: center;
    display: block;
  }
}

.general-style {
  height: 100vh;
  padding: 0 !important;
}

.layout-style {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  min-height: 520px;
  height: 100vh;
  width: 100vw;
}

.intro-column {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  background-color: var(--color-btn-primary-bg);
  min-height: 520px;
  height: 100%;
}

.login-column {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background-color: var(--color-surface);
}

@media (min-width: 600px) {
  .intro-column,
  .login-column {
    flex: 0 0 50%;
    max-width: 50%;
    font-family: "Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif;
  }
}

.beta-badge {
  position: absolute;
  right: 20px;
  top: 18px;
  background: var(--color-primary-mid);
  color: rgb(255, 255, 255);
  text-shadow: 0px 0px 3px rgba(0,0,0,0.3)  ;
  padding: 2px 20px;
  font-style: italic;
  font-weight: 700;
  font-family:'Franklin Gothic Medium', 'Arial Narrow', Arial, sans-serif;
  border-radius: 12px;
  font-size: 1rem;
  z-index: 10;
  box-shadow: 0 1px 1px rgba(0,0,0,0.2);
}

.v-container {
  max-width: none !important;
}

.auth-link {
  color: var(--color-primary-mid);
  cursor: pointer;
  text-decoration: underline;
}
</style>